'use client';

import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import {
  Building2,
  ClipboardList,
  FileCheck2,
  ArrowRight,
  CheckCircle2,
  Zap,
} from 'lucide-react';

const steps = [
  {
    number: '01',
    icon: Building2,
    title: 'Set Up Your Organisation',
    description:
      'Register your organisation, add your OT systems, and invite team members. Define system types and criticality levels.',
    details: [
      'Create organisation profile',
      'Invite assessors and viewers',
      'Add OT systems inventory',
      'Define system criticality',
    ],
    duration: '~15 minutes',
  },
  {
    number: '02',
    icon: ClipboardList,
    title: 'Complete Your Assessment',
    description:
      'Work through all 41 Contributing Outcomes and ~300 IGPs with contextual guidance. Record responses, justifications, and evidence per system.',
    details: [
      'Navigate by Objective and Principle',
      'Record PA/NA responses with justification',
      'Attach evidence per IGP',
      'Track progress in real time',
    ],
    duration: '2–4 weeks typical',
  },
  {
    number: '03',
    icon: FileCheck2,
    title: 'Generate Your Reports',
    description:
      'Export structured PDF and Excel outputs formatted for regulator submission. Use dashboards to identify gaps and remaining work.',
    details: [
      'One-click PDF export',
      'Excel export on demand',
      'Gap analysis dashboard',
      'Progress summary view',
    ],
    duration: 'Under 30 seconds',
  },
];

export function HowItWorksSection() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  return (
    <section className="relative overflow-hidden bg-secondary/30 py-24 lg:py-32" id="how-it-works">
      <div className="absolute inset-0 bg-grid opacity-30" />

      <div
        ref={containerRef}
        className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
      >
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mx-auto max-w-3xl text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent/5 px-4 py-1.5"
          >
            <Zap className="h-4 w-4 text-accent" />
            <span className="font-heading text-sm font-medium text-foreground">
              How It Works
            </span>
          </motion.div>

          <h2 className="font-display text-3xl tracking-tight-display text-foreground sm:text-4xl lg:text-5xl">
            Three steps to{' '}
            <span className="italic text-accent">assessment-ready output</span>
          </h2>

          <p className="mt-6 text-lg text-muted-foreground">
            A structured process from organisation setup through to structured
            outputs — with guided support at every step.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="mt-20">
          <div className="relative">
            {/* Connector line - desktop */}
            <div className="absolute left-0 right-0 top-24 hidden h-0.5 bg-gradient-to-r from-accent via-[hsl(225_60%_45%)] to-success lg:block" />

            <div className="grid gap-8 lg:grid-cols-3 lg:gap-12">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.2 + index * 0.15 }}
                    className="relative"
                  >
                    {/* Step card */}
                    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm lg:p-8">
                      {/* Number badge */}
                      <div className="absolute -top-5 left-6 lg:left-1/2 lg:-translate-x-1/2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary font-heading text-sm font-bold text-primary-foreground shadow-lg ring-4 ring-background">
                          {step.number}
                        </div>
                      </div>

                      {/* Icon */}
                      <div className="mt-4 flex h-14 w-14 items-center justify-center rounded-xl bg-accent/10">
                        <Icon className="h-7 w-7 text-accent" strokeWidth={1.5} />
                      </div>

                      {/* Content */}
                      <h3 className="mt-6 font-heading text-xl font-semibold text-foreground">
                        {step.title}
                      </h3>

                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        {step.description}
                      </p>

                      {/* Details list */}
                      <ul className="mt-6 space-y-2">
                        {step.details.map((detail) => (
                          <li
                            key={detail}
                            className="flex items-center gap-2 text-sm text-foreground"
                          >
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                            {detail}
                          </li>
                        ))}
                      </ul>

                      {/* Duration badge */}
                      <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1.5">
                        <div className="h-1.5 w-1.5 rounded-full bg-accent" />
                        <span className="text-xs font-medium text-muted-foreground">
                          {step.duration}
                        </span>
                      </div>
                    </div>

                    {/* Arrow connector - mobile */}
                    {index < steps.length - 1 && (
                      <div className="flex justify-center py-4 lg:hidden">
                        <ArrowRight className="h-6 w-6 rotate-90 text-border" />
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
